import React, { useRef, useContext } from 'react';
import AppContext from 'context/Context';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faComments, faEdit } from '@fortawesome/free-solid-svg-icons';
import AssignClientSelector from '../AssignClientSelector';

const SidebarCustomerHeader = ({ ticket, onTicketUpdated }) => {
  const { config: { isDark } = {} } = useContext(AppContext);
  const isDarkMode = Boolean(
    isDark ||
    (typeof document !== 'undefined' && (
      document.documentElement.getAttribute('data-bs-theme') === 'dark' ||
      document.documentElement.classList.contains('dark') ||
      document.body.classList.contains('dark')
    ))
  );

  const assignClientTriggerRef = useRef(null);
  const hasClient = Boolean(ticket?.idClienteAkasia);
  const contactName = ticket?.vNombreContacto || ticket?.contactName;
  const username = ticket?.vUsername || ticket?.username;

  const unassignedTitle = contactName || ticket?.customerName || (username ? `@${username}` : 'Asignar cliente');
  const showSubUsername = !hasClient && contactName && username;

  const whatsappDisplay =
    ticket?.vTelefono ||
    ticket?.phone ||
    ticket?.ticketPhone ||
    'Sin WhatsApp';

  const avatarInitials = ticket?.initials || (
    !hasClient && contactName
      ? contactName.trim().split(/\s+/).map(w => w[0]).join('').substring(0, 2).toUpperCase()
      : null
  );

  return (
    <div className="text-center position-relative">
      {/* Avatar circular con iniciales o ícono - Clickeable para cambiar o asignar cliente */}
      <div
        className="rounded-circle d-inline-flex align-items-center justify-content-center fw-bold shadow-none mb-2 position-relative"
        style={{
          width: '64px',
          height: '64px',
          backgroundColor: (ticket?.initials || avatarInitials)
            ? (isDarkMode ? 'rgba(16, 185, 129, 0.2)' : '#dcfce7')
            : (isDarkMode ? '#1e293b' : '#f1f5f9'),
          color: (ticket?.initials || avatarInitials)
            ? (isDarkMode ? '#34d399' : '#15803d')
            : (isDarkMode ? '#94a3b8' : '#64748b'),
          fontSize: '1.25rem',
          cursor: 'pointer',
          transition: 'transform 0.15s ease, box-shadow 0.15s ease'
        }}
        onClick={() => {
          if (typeof assignClientTriggerRef.current === 'function') {
            assignClientTriggerRef.current();
          } else if (assignClientTriggerRef.current?.open) {
            assignClientTriggerRef.current.open();
          }
        }}
        title={hasClient ? 'Clic para cambiar cliente' : 'Clic para asignar cliente'}
      >
        {avatarInitials || <FontAwesomeIcon icon={faUser} />}

        {/* Badge indicador con ícono de edición para saber que se puede modificar */}
        <span
          className="position-absolute rounded-circle shadow-sm d-flex align-items-center justify-content-center text-primary"
          style={{
            bottom: '0px',
            right: '0px',
            width: '22px',
            height: '22px',
            fontSize: '0.65rem',
            backgroundColor: isDarkMode ? '#1e293b' : '#ffffff',
            border: isDarkMode ? '1px solid #233142' : '1px solid #e2e8f0'
          }}
          title={hasClient ? 'Cambiar cliente' : 'Asignar cliente'}
        >
          <FontAwesomeIcon icon={faEdit} />
        </span>
      </div>

      {/* Nombre del cliente o contacto con ID primero, enlace directo y selector */}
      <div className="d-flex align-items-center justify-content-center gap-1 mb-1 flex-wrap">
        {hasClient ? (
          <a
            href={`/clientes/detalle/${ticket.idClienteAkasia}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-decoration-none hover-text-primary"
            style={{
              fontSize: '1rem',
              fontWeight: 600,
              color: isDarkMode ? '#f9fafd' : '#0f172a'
            }}
            title={`Ver cliente #${ticket.idClienteAkasia} en nueva pestaña`}
          >
            #{ticket.idClienteAkasia} - {ticket.customerName || 'Cliente'}
          </a>
        ) : (
          <div className="d-flex flex-column align-items-center">
            {contactName && (
              <span
                className="badge mb-1 px-2 py-0"
                style={{
                  fontSize: '0.68rem',
                  fontWeight: 600,
                  backgroundColor: isDarkMode ? 'rgba(37, 211, 102, 0.15)' : '#ecfdf5',
                  color: isDarkMode ? '#34d399' : '#059669',
                  border: isDarkMode ? '1px solid rgba(52, 211, 153, 0.3)' : '1px solid #a7f3d0',
                  borderRadius: '4px'
                }}
              >
                Nombre WhatsApp
              </span>
            )}
            <span
              style={{
                fontSize: '0.95rem',
                fontWeight: contactName ? 600 : 500,
                color: contactName
                  ? (isDarkMode ? '#f9fafd' : '#0f172a')
                  : (isDarkMode ? '#94a3b8' : '#64748b'),
                cursor: 'pointer'
              }}
              onClick={() => {
                if (typeof assignClientTriggerRef.current === 'function') {
                  assignClientTriggerRef.current();
                } else if (assignClientTriggerRef.current?.open) {
                  assignClientTriggerRef.current.open();
                }
              }}
              title="Clic para asignar cliente"
            >
              {unassignedTitle}
            </span>
            {showSubUsername && (
              <span
                style={{
                  fontSize: '0.80rem',
                  color: isDarkMode ? '#94a3b8' : '#64748b',
                  fontWeight: 500
                }}
              >
                @{username}
              </span>
            )}
            {contactName && (
              <span
                className="text-primary mt-1"
                style={{ fontSize: '0.75rem', cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => {
                  if (typeof assignClientTriggerRef.current === 'function') {
                    assignClientTriggerRef.current();
                  } else if (assignClientTriggerRef.current?.open) {
                    assignClientTriggerRef.current.open();
                  }
                }}
                title="Asignar este ticket a un cliente Akasia"
              >
                Asignar cliente
              </span>
            )}
          </div>
        )}

        {/* Selector de cliente (el trigger de apertura está en el avatar) */}
        <AssignClientSelector
          ticket={ticket}
          onTicketUpdated={onTicketUpdated}
          triggerRef={assignClientTriggerRef}
          hideButton={true}
        />
      </div>

      {/* Teléfono / WhatsApp del cliente */}
      <div className="d-flex flex-column align-items-center gap-1">
        <div className="d-flex align-items-center gap-1" style={{ fontSize: '0.80rem', color: isDarkMode ? '#94a3b8' : '#64748b' }}>
          <FontAwesomeIcon icon={faComments} className="me-1" style={{ color: isDarkMode ? '#34d399' : '#10b981' }} />
          <span>WhatsApp</span>
          <span className="fw-semibold ms-1" style={{ color: isDarkMode ? '#cbd5e1' : '#334155' }}>
            {whatsappDisplay}
          </span>
        </div>
      </div>
    </div>
  );
};

export default SidebarCustomerHeader;
